// Minimal Server-Sent Events reader over fetch — unlike EventSource it can send the region token
// as an Authorization header instead of leaking it into a URL (and so into proxy logs).

export interface SseMessage {
  event: string;
  data: string;
  id?: string;
}

/** Incremental SSE parser: feed it text chunks, it returns every message completed so far. */
export function createSseParser(): (chunk: string) => SseMessage[] {
  let buffer = "";
  return (chunk) => {
    buffer += chunk.replace(/\r\n?/g, "\n");
    const out: SseMessage[] = [];
    let end = buffer.indexOf("\n\n");
    while (end !== -1) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      let event = "message";
      let id: string | undefined;
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (!line || line.startsWith(":")) continue;
        const colon = line.indexOf(":");
        const field = colon === -1 ? line : line.slice(0, colon);
        const value = colon === -1 ? "" : line.slice(colon + 1).replace(/^ /, "");
        if (field === "event") event = value;
        else if (field === "data") data.push(value);
        else if (field === "id") id = value;
      }
      if (data.length) out.push({ event, data: data.join("\n"), ...(id !== undefined ? { id } : {}) });
      end = buffer.indexOf("\n\n");
    }
    return out;
  };
}

/** Streams SSE messages from `url` until the server closes the stream or `signal` aborts. */
export async function streamSse(
  url: string,
  { token, signal, onMessage }: { token: string; signal: AbortSignal; onMessage: (m: SseMessage) => void },
): Promise<void> {
  const res = await fetch(url, {
    headers: { accept: "text/event-stream", authorization: `Bearer ${token}` },
    credentials: "omit",
    signal,
  });
  if (!res.ok || !res.body) throw new Error(`Live events stream failed (${res.status})`);
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  const parse = createSseParser();
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    for (const m of parse(value)) onMessage(m);
  }
}
